/**
 * Firebase client SDK init.
 *
 * In dev (`import.meta.env.DEV`) ci agganciamo automaticamente agli
 * emulatori locali per Auth/Firestore/Storage/Functions. In produzione
 * usiamo la configurazione VITE_FIREBASE_*.
 *
 * Convenzioni:
 * - Region Cloud Functions: 'europe-west8' (Milano) — vedi FN_REGION
 *   server-side e §1.2 design.md.
 * - Persistenza Auth: di default (LOCAL). Non usiamo localStorage per
 *   dati sensibili: Firebase Auth memorizza solo il refresh token cifrato.
 */

import { initializeApp, getApps, type FirebaseApp } from 'firebase/app';
import {
  getAuth,
  connectAuthEmulator,
  type Auth,
} from 'firebase/auth';
import {
  getFirestore,
  connectFirestoreEmulator,
  type Firestore,
} from 'firebase/firestore';
import {
  getStorage,
  connectStorageEmulator,
  type FirebaseStorage,
} from 'firebase/storage';
import {
  getFunctions,
  connectFunctionsEmulator,
  type Functions,
} from 'firebase/functions';
import { EMULATOR_HOST, EMULATOR_PORTS } from './constants';

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY ?? 'demo-key',
  authDomain:
    import.meta.env.VITE_FIREBASE_AUTH_DOMAIN ?? 'demo-dietagest.firebaseapp.com',
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID ?? 'demo-dietagest',
  storageBucket:
    import.meta.env.VITE_FIREBASE_STORAGE_BUCKET ?? 'demo-dietagest.appspot.com',
  messagingSenderId:
    import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID ?? '000000000000',
  appId: import.meta.env.VITE_FIREBASE_APP_ID ?? '1:000000000000:web:demo',
};

export const app: FirebaseApp =
  getApps()[0] ?? initializeApp(firebaseConfig);

export const auth: Auth = getAuth(app);
export const firestore: Firestore = getFirestore(app);
export const storage: FirebaseStorage = getStorage(app);
// Le Cloud Functions sono deployate in europe-west8 (vedi FN_REGION server).
export const functions: Functions = getFunctions(app, 'europe-west8');

let emulatorsConnected = false;

export function connectEmulators(): void {
  if (emulatorsConnected) return;
  emulatorsConnected = true;

  connectAuthEmulator(auth, `http://${EMULATOR_HOST}:${EMULATOR_PORTS.auth}`, {
    disableWarnings: true,
  });
  connectFirestoreEmulator(firestore, EMULATOR_HOST, EMULATOR_PORTS.firestore);
  connectStorageEmulator(storage, EMULATOR_HOST, EMULATOR_PORTS.storage);
  connectFunctionsEmulator(functions, EMULATOR_HOST, EMULATOR_PORTS.functions);
}

if (import.meta.env.DEV) {
  connectEmulators();
}
